import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Text, Box, Heading, VStack, Divider, Image } from '@chakra-ui/react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'sonner';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useNavigate } from 'react-router-dom';
import { InputForm } from '../../../components/inputs/forms/InputForm';
import { FormButton } from '../../../components/buttons/FormButton';
import { api } from '../../../services/api';
import logo from '../../../assets/logo_dark_blue.png';

const schema = yup.object().shape({
  email: yup.string().email('E-mail inválido').required('E-mail obrigatório'),
  password: yup.string().required('Senha obrigatória'),
});

interface ISecaoClienteLoginForm {
  email: string;
  password: string;
}

// Interface de resposta do login
interface ILoginResponse {
  message: string;
  meta: any;
  data: {
    access_token: string;
    refresh_token: string;
    token_type: string;
    cliente: {
      id: number;
      guid: string;
      nome: string;
      email: string;
      empresas: Array<{
        id: number;
        external_id: string;
        razao_social: string;
        nome_fantasia: string;
      }>;
      telefones: Array<any>;
    };
  };
}

// Captcha desabilitado apenas se a variável de ambiente estiver definida
const isCaptchaDisabled = process.env.REACT_APP_DISABLE_CAPTCHA === 'true';

export default function SecaoClienteLoginForm() {
  const navigate = useNavigate();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = React.useRef<any>(null);

  const formMethods = useForm<ISecaoClienteLoginForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onChange',
  });

  const onSubmit = async (values: ISecaoClienteLoginForm) => {
    if (!isCaptchaDisabled && !captchaToken) {
      toast.error('Por favor, complete o captcha antes de enviar.', { position: 'top-center' });
      return;
    }

    try {
      const response = await api.post<ILoginResponse>('/api/v1/secao-cliente/login/', {
        email: values.email,
        password: values.password,
        captcha_token: isCaptchaDisabled ? 'disabled' : captchaToken,
      });

      const { data } = response;

      localStorage.setItem('cliente_access_token', data.data.access_token);
      localStorage.setItem('cliente_refresh_token', data.data.refresh_token);
      localStorage.setItem('cliente_data', JSON.stringify(data.data.cliente));
      toast.success(`Bem-vindo, ${data.data.cliente.nome}!`, { position: 'top-center' });
      navigate('/secao-cliente/dashboard');

      setCaptchaToken(null);
      captchaRef.current?.resetCaptcha();
    } catch (error: any) {
      let errorMessage = 'Erro interno no sistema, tente novamente mais tarde.';
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail[0]?.msg || errorMessage;
      }
      toast.error(errorMessage, { position: 'top-center' });
      setCaptchaToken(null);
      captchaRef.current?.resetCaptcha();
    }
  };

  const handleCaptchaVerification = (token: string) => {
    setCaptchaToken(token);
  };

  const handleCaptchaError = () => {
    toast.error('Erro ao verificar o captcha. Por favor, tente novamente.', {
      position: 'top-center',
    });
  };

  return (
    <Box w="100%">
      <VStack spacing={3} mb={6}>
        <Image src={logo} alt="Logo Especialistas" maxH="200px" mx="auto" />
        <Heading size="lg" color="gray.700">
          Área do Cliente
        </Heading>
        <Text color="gray.500" fontSize="sm" textAlign="center">
          Acesse sua área exclusiva do sistema Especialistas
        </Text>
      </VStack>

      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <FormProvider {...formMethods}>
          <VStack spacing={4} align="stretch">
            <InputForm name="email" label="E-mail" placeholder="Digite seu e-mail" size="lg" />

            <InputForm
              name="password"
              type="password"
              label="Senha"
              placeholder="Digite sua senha"
              size="lg"
            />

            {!isCaptchaDisabled && (
              <>
                <Divider />
                <Box display="flex" justifyContent="center">
                  <HCaptcha
                    sitekey={process.env.REACT_APP_HCAPTCHA_SITEKEY ?? ''}
                    onVerify={handleCaptchaVerification}
                    onError={handleCaptchaError}
                    ref={captchaRef}
                  />
                </Box>
              </>
            )}

            <FormButton
              mt={2}
              isLoading={formMethods.formState.isSubmitting}
              type="submit"
              w="100%"
              size="lg"
              bg="linear-gradient(135deg, #1E3A5F 0%, #2D5A87 100%)"
              _hover={{
                bg: 'linear-gradient(135deg, #162D4A 0%, #1E3A5F 100%)',
                transform: 'translateY(-1px)',
                boxShadow: 'lg',
              }}
              _active={{
                transform: 'translateY(0)',
              }}
              transition="all 0.2s"
            >
              Entrar
            </FormButton>
          </VStack>
        </FormProvider>
      </form>
    </Box>
  );
}
